import { MENU_GROUPS, ADMIN_ONLY_MENUS } from '../adminlayout/menuConfig';

// 상황판 관리 매트릭스의 칸·줄 묶기 계산 (2026-10-24 SituationWidgetMatrix.jsx 에서 옮김).
// 2026-06-17 사이드바 그룹 구조로 컬럼 정렬: 현황 / 직원 / TM·영업 / 시스템 / 회사별 / 통합 메뉴
//   각 그룹 안 menuConfig.js + SidebarContent 외부회사 메뉴 순서대로.
// 2026-10-24 회사 묶음을 DOT·LNJ 고정 → **보고 있는 테넌트의 회사**(서버가 준 companies)로, 이름은 작업 대상과 같은 회사 이름.
//   기본 틀(운영사) 화면은 본사(대표 회사)를 뺀 회사 — 본사 열쇠는 위 사이드바 묶음에 있다(파인그룹 = DOT·LNJ 그대로).
//   테넌트 화면은 그 테넌트 회사 전부(대표 회사의 '본사만 보는 일일콜' 열쇠도 그 회사 묶음으로).

// SidebarContent.jsx 의 외부회사 메뉴 패턴 순서와 동기 유지.
const EXT_MENU_PERM_PATTERNS = [
  (cc) => `EXTERNAL_${cc}_USER_MANAGEMENT`,
  (cc) => `EXTERNAL_${cc}_ATTENDANCE_MANAGEMENT`,
  (cc) => `EXTERNAL_${cc}_ATTENDANCE_CALENDAR_MANAGEMENT`,
  (cc) => `EXTERNAL_${cc}_LEAVE_MANAGEMENT`,
  (cc) => `EXTERNAL_${cc}_TM_CALL_MANAGEMENT`,
  (cc) => `EXTERNAL_${cc}_CONTRACT_MANAGEMENT`, // 회사별 계약 관리(2026-10-15)
  (cc) => `EXTERNAL_${cc}_PROMOTIONAL_MANAGEMENT`, // 회사별 홍보물 관리(2026-10-19)
  (cc) => `CONTRACT_CANCEL_${cc}_MANAGEMENT`,
  (cc) => `CONTRACT_RENEWAL_${cc}_MANAGEMENT`,
  (cc) => `MARKETER_CONSULTANT_${cc}_MGMT`,
  (cc) => `REVENUE_${cc}_MANAGEMENT`,
  (cc) => `SETTLEMENT_${cc}_MANAGEMENT`,
  (cc) => `PAYOUT_${cc}_MANAGEMENT`,
  (cc) => `PAYROLL_${cc}_MANAGEMENT`,
  (cc) => `INCENTIVE_${cc}_MANAGEMENT`,
  (cc) => `EXTERNAL_${cc}_TERMINAL_MANAGEMENT`,
];

// 회사 묶음 색 — 순서대로(대표 회사가 앞). 파인그룹은 파인·DOT·LNJ 가 예전 색 그대로(초록·노랑·남색).
const COMPANY_TONES = [
  'bg-emerald-50 text-emerald-700',
  'bg-amber-50 text-amber-700',
  'bg-indigo-50 text-indigo-700',
  'bg-rose-50 text-rose-700',
  'bg-cyan-50 text-cyan-700',
  'bg-lime-50 text-lime-700',
];
const COMMON_TONE = 'bg-slate-100 text-slate-700';
const INTEGRATED_TONE = 'bg-purple-50 text-purple-700';

const upper = (v) => String(v || '').trim().toUpperCase();

// 칸 묶음에 쓸 회사 — 기본 틀은 대표 회사 빼고, 테넌트 화면은 전부
function columnCompanies(companies, mode) {
  return (Array.isArray(companies) ? companies : []).filter((c) => mode === 'tenant' || !c.is_primary);
}

/** 위젯 줄 묶음 키: 'common' | 'integrated' | 회사코드. 이름 끝 조각이 회사(_pine = 본사)면 그 회사. */
export function classifyWidget(code, companyCodes = new Set(), hqCode = null) {
  if (!code) return 'common';
  if (code.startsWith('admin_integrated_')) return 'integrated';
  const m = /_([a-z0-9]+)$/.exec(code);
  if (!m) return 'common';
  const frag = upper(m[1]);
  const company = frag === 'PINE' && hqCode ? upper(hqCode) : frag;
  return companyCodes.has(company) ? company : 'common';
}

function groupBy(items, getKey) {
  const map = {};
  items.forEach((item) => {
    const key = getKey(item);
    if (!map[key]) map[key] = [];
    map[key].push(item);
  });
  return map;
}

// 사이드바 그룹 정의 — 각 그룹의 permCode 순서 list 생성.
//   menuConfig.MENU_GROUPS = 현황/직원/TM·영업/시스템
//   회사 그룹 = SidebarContent 외부회사 패턴(회사 이름으로)
//   통합 메뉴 그룹 = INTEGRATED_MENU_* prefix
function buildSidebarPermOrder(companies, mode, nameOf) {
  const groups = [];

  // MENU_GROUPS — 사이드바 PINE/admin 메뉴
  for (const grp of MENU_GROUPS) {
    const codes = [];
    for (const item of grp.items) {
      if (item.permCode) codes.push(item.permCode);
      if (Array.isArray(item.permCodeAny)) {
        for (const c of item.permCodeAny) codes.push(c);
      }
    }
    // 시스템 그룹은 admin 전용 메뉴 (통합 메뉴 관리 등) 추가
    if (grp.group === '시스템') {
      for (const m of ADMIN_ONLY_MENUS) if (m.permCode) codes.push(m.permCode);
    }
    groups.push({ key: `sidebar-${grp.group}`, label: grp.group, codes });
  }

  // 회사 그룹 — 보고 있는 테넌트의 회사(2026-10-24, 예전엔 DOT·LNJ 고정). 직원 본인 일일콜 열쇠도 그 회사 묶음.
  for (const co of columnCompanies(companies, mode)) {
    const cc = upper(co.code);
    const codes = [...EXT_MENU_PERM_PATTERNS.map((fn) => fn(cc)), `TM_FEATURE_EXT_${cc}`];
    groups.push({ key: `ext-${cc}`, label: nameOf ? nameOf(cc) : cc, codes });
  }

  return groups;
}

// 통합 메뉴 perm 정렬: INTEGRATED_MENU_{id} 의 id 오름차순
function sortIntegrated(perms) {
  return [...perms].sort((a, b) => {
    const ai = Number(a.code.replace('INTEGRATED_MENU_', '')) || 0;
    const bi = Number(b.code.replace('INTEGRATED_MENU_', '')) || 0;
    return ai - bi;
  });
}

// 권한 그룹: 사이드바 그룹 순서 + 회사 + 통합 메뉴 + 마지막에 미분류
export function orderPermGroups(permissions, { companies = [], mode = 'global', nameOf = null } = {}) {
  const permMap = new Map();
  for (const p of permissions) permMap.set(p.code, p);
  const used = new Set();
  const sidebarGroups = buildSidebarPermOrder(companies, mode, nameOf);
  const result = [];

  for (const grp of sidebarGroups) {
    const items = [];
    for (const code of grp.codes) {
      const p = permMap.get(code);
      if (p && !used.has(code)) {
        items.push(p);
        used.add(code);
      }
    }
    if (items.length > 0) result.push({ key: grp.key, label: grp.label, items });
  }

  // 통합 메뉴 그룹
  const integrated = sortIntegrated(permissions.filter((p) => p.code.startsWith('INTEGRATED_MENU_') && !used.has(p.code)));
  integrated.forEach((p) => used.add(p.code));
  if (integrated.length > 0) result.push({ key: 'integrated', label: '통합 메뉴', items: integrated });

  // 미분류 (사이드바에 없는 권한) 마지막에
  const others = permissions.filter((p) => !used.has(p.code));
  if (others.length > 0) result.push({ key: 'others', label: '기타', items: others });

  return result;
}

// 위젯 줄 묶음 — 공통 → 회사(대표 회사 먼저) → 통합 메뉴, 빈 묶음은 뺀다. 이름·색도 함께.
export function orderWidgetGroups(widgets, { companies = [], hqCode = null, nameOf = null } = {}) {
  const list = Array.isArray(companies) ? companies : [];
  const codes = new Set(list.map((c) => upper(c.code)));
  const widgetGroups = groupBy(widgets, (w) => classifyWidget(w.code, codes, hqCode));
  const order = [
    { key: 'common', label: '공통', tone: COMMON_TONE },
    ...list.map((c, i) => ({ key: upper(c.code), label: nameOf ? nameOf(c.code) : upper(c.code), tone: COMPANY_TONES[i % COMPANY_TONES.length] })),
    { key: 'integrated', label: '통합 메뉴', tone: INTEGRATED_TONE },
  ];
  return order
    .map((g) => ({ ...g, items: widgetGroups[g.key] || [] }))
    .filter((g) => g.items.length > 0);
}

// 위젯별 체크된 권한 갯수
export function countWidgetChecks(permissions, connectionMap) {
  const map = {};
  for (const perm of permissions) {
    const codes = connectionMap[perm.code] || [];
    for (const wcode of codes) {
      map[wcode] = (map[wcode] || 0) + 1;
    }
  }
  return map;
}
